import React from 'react';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { BodyText, DisplayText, useResponsive } from '@garnish/core';
import { ContentfulImage, ContentfulImageTransformOptions } from '@garnish/media';

import { FlexLandingPageContent } from './FlexLandingPageContent';

export const FlexLandingPageGuide = (props: FlexLandingPageGuideProps) => {
  const { guideTitle, guideEntries } = props;
  const { match } = useResponsive();

  if (guideEntries.length === 0) return null;

  return (
    <FlexLandingPageContent style={match([styles.containerXS, styles.containerSM])}>
      <DisplayText sizeMatch={['40']} style={styles.title}>
        {guideTitle}
      </DisplayText>

      <View style={match([styles.entriesXS, styles.entriesXS, styles.entriesLG])}>
        {guideEntries.map((entry, index) => (
          <View key={entry.id} style={styles.entry}>
            <View style={styles.entryImageWrapper}>
              <ContentfulImage
                baseUrl={entry.imageUrl}
                aria-label={entry.imageA11y}
                style={styles.image}
                options={CONTENTFUL_OPTIONS}
                contentFit="contain"
                contentPosition="bottom"
              />
            </View>

            <View style={styles.entryContent}>
              <View style={styles.entryTitleWrapper}>
                <View style={styles.entryNumberWrapper}>
                  <BodyText sizeMatch={['18']} style={styles.entryNumber}>
                    {index + 1}
                  </BodyText>
                </View>

                <DisplayText sizeMatch={['24']} style={styles.entryTitle}>
                  {entry.title}
                </DisplayText>
              </View>

              <BodyText sizeMatch={['14', '16']} style={styles.description}>
                {entry.description}
              </BodyText>
            </View>
          </View>
        ))}
      </View>
    </FlexLandingPageContent>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const IMAGE_HEIGHT = 156;
const CONTENTFUL_OPTIONS: Partial<ContentfulImageTransformOptions> = {
  h: IMAGE_HEIGHT,
  q: 70,
  fm: 'webp',
  fit: 'fill',
};

const styles = StyleSheet.create({
  containerXS: {
    gap: theme.spacing['8'],
    paddingVertical: theme.spacing['4'],
  },
  containerSM: {
    gap: theme.spacing['8'],
    paddingVertical: theme.spacing['8'],
  },
  title: {
    color: theme.colors.CHARCOAL,
    textAlign: 'center',
  },
  entriesXS: {
    alignItems: 'center',
    gap: theme.spacing['6'],
  },
  entriesLG: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: theme.spacing['4'],
  },
  entry: {
    flex: 1,
    maxWidth: 411,
    overflow: 'hidden',
    borderColor: theme.colors.SPINACH,
    borderRadius: theme.radius.large,
    borderWidth: 1,
  },
  entryImageWrapper: {
    backgroundColor: theme.colors.SPINACH,
    paddingHorizontal: theme.spacing['5'],
    paddingTop: theme.spacing['6'],
  },
  image: {
    width: '100%',
    height: IMAGE_HEIGHT,
    borderTopLeftRadius: theme.radius.large,
    borderTopRightRadius: theme.radius.large,
  },
  entryContent: {
    gap: theme.spacing['2'],
    padding: theme.spacing['4'],
  },
  entryTitleWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['3'],
  },
  entryNumberWrapper: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.CUCUMBER,
    borderWidth: 3,
    borderColor: theme.colors.AVOCADO,
    borderRadius: theme.radius['3xLarge'],
  },
  entryNumber: {
    color: theme.colors.SPINACH,
  },
  entryTitle: {
    color: theme.colors.SPINACH,
    marginTop: theme.spacing['1'],
  },
  description: {
    color: theme.colors.CHARCOAL,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type FlexLandingPageGuideProps = {
  guideTitle: string;
  guideEntries: {
    id: string;
    title: string;
    description: string;
    imageUrl: string;
    imageA11y: string;
  }[];
};
